import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { pageAction } from '../redux_files/actions/page_actions';
import { createOrder, clearOrderConfirmation } from '../redux_files/actions/order_actions';
import { updateOrderFormField, setOrderFormError, setOrderFormStep, resetOrderForm, clearOrderFormError } from '../redux_files/actions/orderFormActions';
import OrderStep from '../components/order_step';
import DeliveryDateStep from '../components/order-steps/DeliveryDateStep';
import MealTypeStep from '../components/order-steps/MealTypeStep';
import PlateSizeStep from '../components/order-steps/PlateSizeStep';
import QuantityStep from '../components/order-steps/QuantityStep';
import DeliveryDetailsStep from '../components/order-steps/DeliveryDetailsStep';
import OrderSummaryStep from '../components/order-steps/OrderSummaryStep';
import { useMealAvailability } from '../hooks/useMealAvailability';
import qrCodeImage from '../assets/QRCode.jpg';
import { getWhatsAppLink, PHONE_NUMBER } from '../utils/constants';

// Constants for delivery charge calculation
const FREE_DELIVERY_RADIUS_KM = 7;
const DELIVERY_CHARGE_PER_KM = 5;

const OrderFood = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orderConfirmation: reduxOrderConfirmation, error: orderError } = useSelector(state => state.order);
  const formData = useSelector(state => state.orderForm);
  const { currentStep, selectedDate, mealType, plateSize, numberOfPlates, deliveryDistance, selectedDeliveryCoords, name, phone, email, address, deliveryLocation } = formData;
  
  // Local UI state only
  const [orderConfirmation, setOrderConfirmation] = React.useState(null);
  const [orderSubmissionError, setOrderSubmissionError] = React.useState('');
  const [redirectCountdown, setRedirectCountdown] = React.useState(60);
  const [retryCountdown, setRetryCountdown] = React.useState(60);
  const totalSteps = 6;

  // Check meal availability based on current time
  const mealAvailability = useMealAvailability(selectedDate);

  // Continuously check and update meal selection based on availability
  useEffect(() => {
    // If currently selected meal becomes unavailable, auto-switch or clear selection
    if (mealType === 'lunch' && !mealAvailability.lunchAvailable) {
      if (mealAvailability.dinnerAvailable) {
        dispatch(updateOrderFormField('mealType', 'dinner'));
      } else {
        dispatch(updateOrderFormField('mealType', null));
      }
    } else if (mealType === 'dinner' && !mealAvailability.dinnerAvailable) {
      if (mealAvailability.lunchAvailable) {
        dispatch(updateOrderFormField('mealType', 'lunch'));
      } else {
        dispatch(updateOrderFormField('mealType', null));
      }
    }
  }, [mealAvailability.lunchAvailable, mealAvailability.dinnerAvailable, mealType, dispatch]);

  useEffect(() => {
    document.title = 'Order Food';
    dispatch(pageAction('Order Food'));
    // Clear any stale order confirmation when component mounts
    dispatch(clearOrderConfirmation());
    // Reset order form to start from step 1
    dispatch(resetOrderForm());
    // Ensure we always start at Step 1 when arriving here
    dispatch(setOrderFormStep(1));

    // Disable hardware back button
    window.history.pushState(null, null, window.location.pathname);

    const handlePopState = () => {
      // Prevent going back and push the same state again
      window.history.pushState(null, null, window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [dispatch]);

  // Auto-select available meal based on current time whenever selectedDate changes
  useEffect(() => {
    if (!selectedDate) return;
    const today = new Date();
    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const isToday = selectedDate === todayString;

    let lunchAvailable = true;
    let dinnerAvailable = true;

    if (isToday) {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const totalMinutes = hours * 60 + minutes;

      // Lunch disabled: After 12:00 PM (720 minutes)
      const lunchDisabledStart = 12 * 60;
      const lunchDisabled = totalMinutes >= lunchDisabledStart;

      // Dinner disabled: After 5:30 PM (1050 minutes)
      const dinnerDisabledStart = 17 * 60 + 30;
      const dinnerDisabled = totalMinutes >= dinnerDisabledStart;

      lunchAvailable = !lunchDisabled;
      dinnerAvailable = !dinnerDisabled;
    }

    if (!lunchAvailable && !dinnerAvailable) {
      dispatch(updateOrderFormField('mealType', null));
    } else if (!lunchAvailable && dinnerAvailable) {
      dispatch(updateOrderFormField('mealType', 'dinner'));
    } else {
      dispatch(updateOrderFormField('mealType', 'lunch'));
    }
  }, [selectedDate, dispatch]);

  // If today is closed (after 5:30 PM) and no date selected, default to tomorrow
  useEffect(() => {
    const now = new Date();
    const minutesNow = now.getHours() * 60 + now.getMinutes();
    const cutoffMinutes = 17 * 60 + 30; // 5:30 PM
    const isAfterCutoff = minutesNow >= cutoffMinutes;
    if (currentStep === 1 && isAfterCutoff && !selectedDate) {
      const tmr = new Date(Date.now() + 24 * 60 * 60 * 1000);
      const ymd = `${tmr.getFullYear()}-${String(tmr.getMonth() + 1).padStart(2, '0')}-${String(tmr.getDate()).padStart(2, '0')}`;
      dispatch(updateOrderFormField('selectedDate', ymd));
      dispatch(updateOrderFormField('mealType', 'lunch'));
      dispatch(clearOrderFormError('date'));
      dispatch(clearOrderFormError('meal'));
    }
  }, [currentStep, selectedDate, dispatch]);

  // Hide delivery location error banner once a location is selected
  useEffect(() => {
    const hasLocationText = !!(deliveryLocation && deliveryLocation.trim());
    const hasCoords = !!(selectedDeliveryCoords && selectedDeliveryCoords.lat != null && selectedDeliveryCoords.lng != null);
    if (hasLocationText || hasCoords) {
      dispatch(clearOrderFormError('deliveryLocation'));
      setOrderSubmissionError('');
    }
  }, [deliveryLocation, selectedDeliveryCoords, dispatch]);

  // Hide Name/Phone/Address banner once fields become valid
  useEffect(() => {
    const nameValid = !!(name && name.trim() && /^[a-zA-Z\s]+$/.test(name.trim()));
    const phoneValid = !!(phone && phone.trim() && /^[6-9]\d{9}$/.test(phone.trim()));
    const addressValid = !!(address && address.trim());

    if (nameValid) {
      dispatch(clearOrderFormError('name'));
      setOrderSubmissionError('');
    }
    if (phoneValid) {
      dispatch(clearOrderFormError('phone'));
      setOrderSubmissionError('');
    }
    if (addressValid) {
      dispatch(clearOrderFormError('address'));
      setOrderSubmissionError('');
    }
  }, [name, phone, address, dispatch]);

  // Calculate price based on plate type and quantity
  const calculatePrice = () => {
    const plates = numberOfPlates === '' ? 0 : numberOfPlates;
    let platePrice = 0;
    let deliveryCharge = 0;

    if (plateSize === 'half') {
      platePrice = 50;
    } else {
      // Full plate pricing
      if (plates > 15) {
        platePrice = 55;
      } else if (plates > 6) {
        platePrice = 60;
      } else {
        platePrice = 70;
      }
    }

    // Calculate delivery charge based on distance
    if (deliveryDistance !== null) {
      if (deliveryDistance <= FREE_DELIVERY_RADIUS_KM) {
        // Within 7km - free delivery
        deliveryCharge = 0;
      } else {
        // Outside 7km
        if (plates >= 2) {
          // 2 or more orders - free delivery
          deliveryCharge = 0;
        } else if (plates === 1) {
          // Single order - ₹5 per km for distance beyond 7km
          const chargeableDistance = deliveryDistance - FREE_DELIVERY_RADIUS_KM;
          deliveryCharge = Math.ceil(chargeableDistance) * DELIVERY_CHARGE_PER_KM;
        }
      }
    }

    const subtotal = platePrice * plates;
    const total = subtotal + deliveryCharge;

    return {
      platePrice,
      subtotal,
      deliveryCharge,
      total,
      distance: deliveryDistance
    };
  };

  const pricing = calculatePrice();

  // Build order data from current form state
  const buildOrderData = () => ({
    // Order Details
    orderDate: new Date().toISOString(),
    deliveryDate: selectedDate,
    mealType: mealType,
    plateType: plateSize === 'full' ? 'Full Plate' : 'Buffet',
    numberOfPlates: numberOfPlates,

    // Pricing
    platePrice: pricing.platePrice,
    subtotal: pricing.subtotal,
    deliveryCharge: pricing.deliveryCharge,
    totalAmount: pricing.total,

    // Customer Details
    customerName: name,
    customerPhone: phone,
    customerEmail: email,

    // Delivery Details
    deliveryAddress: address,
    deliveryLocation: deliveryLocation,
    deliveryCoordinates: {
      latitude: selectedDeliveryCoords?.lat || null,
      longitude: selectedDeliveryCoords?.lng || null
    },

    // Order Status
    status: 'pending',
    paymentStatus: 'pending'
  });

  const handleNext = () => {
    // Validate meal selection - check if selected meal is available
    if (currentStep === 1) {
      // Check if no meal is selected (both closed)
      if (!mealType) {
        dispatch(setOrderFormError('meal', 'All meals are closed for today. Please select a different date.'));
        return;
      }

      const isLunchSelected = mealType === 'lunch';
      const isDinnerSelected = mealType === 'dinner';
      
      if (isLunchSelected && !mealAvailability.lunchAvailable) {
        dispatch(setOrderFormError('meal', 'Sorry, Lunch is closed. Please select Dinner.'));
        return;
      }
      if (isDinnerSelected && !mealAvailability.dinnerAvailable) {
        dispatch(setOrderFormError('meal', 'Sorry, Dinner is closed. Please select Lunch.'));
        return;
      }
    }
    
    if (currentStep === 3 && (numberOfPlates === '' || numberOfPlates === 0)) {
      dispatch(setOrderFormError('plate', 'Please select at least 1 plate'));
      return;
    }

    if (currentStep === 5) {
      let hasError = false;
      
      if (!name.trim()) {
        dispatch(setOrderFormError('name', 'Name is required'));
        hasError = true;
      } else if (!/^[a-zA-Z\s]+$/.test(name.trim())) {
        dispatch(setOrderFormError('name', 'Name should contain only letters and spaces'));
        hasError = true;
      }
      
      if (!phone.trim()) {
        dispatch(setOrderFormError('phone', 'Phone number is required'));
        hasError = true;
      } else if (!/^[6-9]\d{9}$/.test(phone.trim())) {
        dispatch(setOrderFormError('phone', 'Please enter a valid 10-digit phone number'));
        hasError = true;
      }
      
      if (!address.trim()) {
        dispatch(setOrderFormError('address', 'Address is required'));
        hasError = true;
      }
      
      if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        dispatch(setOrderFormError('email', 'Please enter a valid email address'));
        hasError = true;
      }
      
      if (!deliveryLocation.trim()) {
        dispatch(setOrderFormError('deliveryLocation', 'Delivery location is required'));
        hasError = true;
      }
      
      if (hasError) {
        return;
      }
    }
    
    if (currentStep < totalSteps) {
      dispatch(setOrderFormStep(currentStep + 1));
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      dispatch(setOrderFormStep(currentStep - 1));
    }
  };

  const handleOrderSubmit = async () => {
    if (numberOfPlates === '' || numberOfPlates === 0) {
      dispatch(setOrderFormError('plate', 'Please select at least 1 plate'));
      dispatch(setOrderFormStep(3));
      setOrderSubmissionError('Please select at least 1 plate');
      return;
    }

    // Validate Name
    if (!name.trim()) {
      dispatch(setOrderFormError('name', 'Name is required'));
      dispatch(setOrderFormStep(5));
      setOrderSubmissionError('Name is required');
      return;
    } else if (!/^[a-zA-Z\s]+$/.test(name.trim())) {
      dispatch(setOrderFormError('name', 'Name should contain only letters and spaces'));
      dispatch(setOrderFormStep(5));
      setOrderSubmissionError('Name should contain only letters and spaces');
      return;
    }

    // Validate Phone
    if (!phone.trim()) {
      dispatch(setOrderFormError('phone', 'Phone number is required'));
      dispatch(setOrderFormStep(5));
      setOrderSubmissionError('Phone number is required');
      return;
    } else if (!/^[6-9]\d{9}$/.test(phone.trim())) {
      dispatch(setOrderFormError('phone', 'Please enter a valid 10-digit phone number'));
      dispatch(setOrderFormStep(5));
      setOrderSubmissionError('Please enter a valid 10-digit phone number');
      return;
    }

    // Validate Address
    if (!address.trim()) {
      dispatch(setOrderFormError('address', 'Address is required'));
      dispatch(setOrderFormStep(5));
      setOrderSubmissionError('Address is required');
      return;
    }

    // Require a delivery location selection before confirming
    const hasCoords = !!(selectedDeliveryCoords && selectedDeliveryCoords.lat != null && selectedDeliveryCoords.lng != null);
    if (!deliveryLocation || !deliveryLocation.trim() || !hasCoords) {
      dispatch(setOrderFormError('deliveryLocation', 'Please select a delivery location'));
      dispatch(setOrderFormStep(5));
      setOrderSubmissionError('Please select a delivery location');
      return;
    }

    // Prepare order data
    const orderData = buildOrderData();

    // Dispatch Redux action instead of direct fetch
    // Move to Order Summary screen while submitting
    dispatch(setOrderFormStep(6));
    dispatch(createOrder(orderData));
    // Clear any prior submission banner when submitting
    setOrderSubmissionError('');
  };

  // Allow retry after 1 minute on Order Summary when there is an error
  useEffect(() => {
    if (currentStep === 6 && orderSubmissionError && !orderConfirmation) {
      if (retryCountdown === 0) return;
      const timer = setTimeout(() => setRetryCountdown(retryCountdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      // Reset countdown when leaving summary or error clears
      if (retryCountdown !== 60) setRetryCountdown(60);
    }
  }, [currentStep, orderSubmissionError, orderConfirmation, retryCountdown]);

  const handleRetryOrder = () => {
    if (retryCountdown > 0) return; // Guard
    const orderData = buildOrderData();
    setOrderSubmissionError('');
    dispatch(createOrder(orderData));
  };

  // Listen for order success/failure from Redux
  useEffect(() => {
    if (reduxOrderConfirmation) {
      setOrderConfirmation({
        orderId: reduxOrderConfirmation.orderId || 'N/A',
        mealLabel: `${numberOfPlates} ${plateSize === 'full' ? 'Full Plate' : 'Buffet'} (${mealType ? mealType.charAt(0).toUpperCase() + mealType.slice(1) : 'N/A'})`,
        total: pricing.total
      });
      setOrderSubmissionError('');
      setRedirectCountdown(60); // Reset countdown to 60 seconds
    }
    
    if (orderError) {
      setOrderSubmissionError(orderError);
      // Keep user on Order Summary screen to view error
      dispatch(setOrderFormStep(6));
    }
  }, [reduxOrderConfirmation, orderError, numberOfPlates, plateSize, mealType, pricing.total, dispatch]);

  // Auto-redirect to home page after 1 minute with countdown
  useEffect(() => {
    if (!orderConfirmation) return;

    if (redirectCountdown === 0) {
      // Clear order confirmation and local state before navigating
      setOrderConfirmation(null);
      setOrderSubmissionError('');
      dispatch(clearOrderConfirmation());
      navigate('/');
      return;
    }

    const timer = setTimeout(() => {
      setRedirectCountdown(redirectCountdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [orderConfirmation, redirectCountdown, navigate, dispatch]);

  const handleStartNewOrder = () => {
    setOrderConfirmation(null);
    setOrderSubmissionError('');
    // Clear any existing order confirmation in Redux and reset timers
    dispatch(clearOrderConfirmation());
    setRedirectCountdown(60);
    setRetryCountdown(60);
    dispatch(resetOrderForm());
    dispatch(setOrderFormStep(1));
  };

  if (orderConfirmation) {
    return (
      <div className='container'>
        <div className='order-status-screen'>
          <div className='status-header'>
            {/* <div className='status-badge'>Order Received</div> */}
            <h2 className='section-title'>Thank you For your order!</h2>
            {/* <p className='status-message'>Our team has received your request and your order would be delivered as per delivery timings.<br />Lunch Delivery Time: 12:00 - 2:00 PM<br />Dinner Delivery Time: 7:00 - 9:00 PM</p> */}
          </div>

          {/* <div className='order-status-details'>
            <div><span className='detail-label'>Order ID:</span> <span className='detail-value'>{orderConfirmation.orderId}</span></div>
            <div><span className='detail-label'>Items:</span> <span className='detail-value'>{orderConfirmation.mealLabel}</span></div>
            <div><span className='detail-label'>Amount:</span> <span className='detail-value'>₹{orderConfirmation.total}</span></div>
          </div> */}
          <div className='order-redirect-info'>
            <strong>ℹ️ Redirecting to Home Page</strong>
            <div className='order-redirect-countdown'>
              in {Math.floor(redirectCountdown / 60)}:{String(redirectCountdown % 60).padStart(2, '0')}
            </div>
            <div className='order-redirect-message'>You will be redirected shortly...</div>
          </div>
          <div className='qr-text'>
              <strong>Scan this QR code</strong>
              <p>For Payment details please contact <a href={getWhatsAppLink()} target='_blank' rel="noreferrer" className='whatsApp-inline'>{PHONE_NUMBER}</a></p>
          </div>
          <div className='qr-block'>
            <img src={qrCodeImage} alt='Scan QR for order' className='qr-image' />
            
          </div>
          <div className='status-actions'>
            <button className='nav-button next-button' onClick={handleStartNewOrder}>
              Place Another Order
            </button>
          </div>
        </div>
      </div>
    ); 
  }

  return (
    <>
      <div className='container'>
        <div className='order-food-wrapper'>
        
        {/* Progress Indicator */}
        <div className='progress-indicator'>
          <div 
            className='progress-steps'
            style={{ '--progress-percentage': `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
          >
            {[1, 2, 3, 4, 5, 6].map((step) => (
              <div 
                key={step} 
                className={`progress-step ${currentStep === step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}
              >
                <div className='step-number'>{step}</div>
              </div>
            ))}
          </div>
        </div>

        {/* COMMENTED OUT FOR FUTURE USE - Step 1: Select Date */}
        
        {currentStep === 1 && (
           <OrderStep title='Select Delivery Date'>
            <DeliveryDateStep />
          </OrderStep>
        )}
       

        {/* Step 2: Select Meal Type (was Step 2) */}
        {currentStep === 2 && (
          <OrderStep title='Select Meal Type'>
            <MealTypeStep />
          </OrderStep>
        )}

        {/* Step 3: Select Plate Size */}
        {currentStep === 3 && (
          <OrderStep title='Select Meal Plan'>
            <PlateSizeStep />
          </OrderStep>
        )}

        {/* Step 4: Number of Plates */}
        {currentStep === 4 && (
          <OrderStep title='How Many Plates?'>
            <QuantityStep />
          </OrderStep>
        )}

        {/* Step 5: Delivery Details */}
        {currentStep === 5 && (
          <OrderStep title='Delivery Details'>
            <DeliveryDetailsStep />
          </OrderStep>
        )}

        {/* Step 6: Confirm Order */}
        {currentStep === 6 && (
          <OrderStep title='Order Summary'>
            <OrderSummaryStep />
          </OrderStep>
        )}

        {/* Navigation Buttons */}
        {currentStep === 6 && orderSubmissionError && (
          <div className='order-submission-error'>
            <strong>⚠️ Error:</strong> {orderSubmissionError}
          </div>
        )}
          <div className='step-navigation mt-30'>
          <button 
            className={`nav-button prev-button ${currentStep === 1 ? 'disabled' : ''}`}
            onClick={handlePrevious}
            disabled={currentStep === 1}
          >
            ← Previous
          </button>
          
          {currentStep < totalSteps ? (
            <button 
              className={`nav-button next-button ${((currentStep === 1 && !selectedDate) || (currentStep === 2 && !mealType) || (currentStep === 3 && (numberOfPlates === '' || numberOfPlates === 0))) ? 'disabled' : ''}`}
              onClick={handleNext}
              disabled={(currentStep === 1 && !selectedDate) || (currentStep === 2 && !mealType) || (currentStep === 3 && (numberOfPlates === '' || numberOfPlates === 0))}
            >
              Next →
            </button>
          ) : (
            <button 
              className='nav-button submit-button'
              onClick={handleOrderSubmit}
            >
              Confirm Order
            </button>
          )}
          </div>
           {currentStep === 6 && !orderConfirmation && orderSubmissionError && (
            <div className='retry-container'>
              <button
                className={`nav-button retry-button ${retryCountdown > 0 ? 'disabled' : ''}`}
                onClick={handleRetryOrder}
                disabled={retryCountdown > 0}
              >
                {retryCountdown > 0
                  ? `Retry in ${Math.floor(retryCountdown / 60)}:${String(retryCountdown % 60).padStart(2, '0')}`
                  : 'Retry'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OrderFood;
